import { TraceMap, originalPositionFor } from '@jridgewell/trace-mapping'

/**
 * Расшифровывает минифицированный стек (`error.stack`) в исходные файлы и строки.
 *
 * Дело не в сорсмапах — они в сборке есть и исправны (`vite.config.ts: build.sourcemap: true`).
 * Дело в том, что браузер никогда не переписывает саму строку `error.stack` по сорсмапу: он
 * резолвит её только для показа в панели DevTools, а строка как лежала минифицированной кашей
 * (`at Ac (https://host/assets/index-B3LkfuIU.js:50:3582)`), так и остаётся. Расшифровывать
 * приходится самим — в момент ошибки, в браузере пользователя, тем же способом, каким это
 * делают DevTools: скачать `<бандл>.js.map` и прогнать позицию через него.
 *
 * Это best-effort и не имеет права падать или зависать: отчёт со «Скрин смерти» — последнее,
 * что остаётся у пользователя для отладки, ронять его расшифровкой нельзя. Если карту не
 * удалось загрузить (её не раздают в проде, нет сети, 404) или позиция в ней не нашлась —
 * соответствующая строка стека остаётся как есть, без расшифровки.
 */
export async function symbolicateStack(stack: string): Promise<string> {
  const lines = stack.split('\n')
  const symbolicated = await Promise.all(lines.map(symbolicateLine))
  return symbolicated.join('\n')
}

// Кадр стека с адресом бандла где угодно в строке: `(https://host/assets/x.js:50:3582)`
// (Chrome, «at fn (url:L:C)»), `https://host/assets/x.js:50:3582` без скобок (Chrome,
// «at url:L:C», часто для верхнеуровневого кода) или `fn@https://host/assets/x.js:50:3582`
// (Firefox). Все три формата содержат один и тот же суффикс `url:L:C` — ищем его напрямую,
// не завязываясь на то, что стоит перед адресом.
const FRAME_LOCATION_RE = /(https?:\/\/[^\s()]+\.js):(\d+):(\d+)/g

async function symbolicateLine(line: string): Promise<string> {
  const matches = [...line.matchAll(FRAME_LOCATION_RE)]
  if (matches.length === 0) return line

  let result = ''
  let cursor = 0

  for (const match of matches) {
    const { index: start } = match
    const [full, url, rawLine, rawColumn] = match
    if (start === undefined || full === undefined || url === undefined) continue
    if (rawLine === undefined || rawColumn === undefined) continue

    result += line.slice(cursor, start)
    result += (await resolveOriginalLocation(url, Number(rawLine), Number(rawColumn))) ?? full
    cursor = start + full.length
  }

  result += line.slice(cursor)
  return result
}

/**
 * Резолвит один кадр в `source:line:column` исходника, либо null, если не вышло.
 *
 * Имя функции (`at Cl (…)`) осознанно НЕ восстанавливаем, хотя соблазн есть и поле `name`
 * в сорсмапе существует. Проверено на живой сборке: в позиции кадра `originalPositionFor`
 * возвращает `name: null` — а там, где имя всё-таки есть, это идентификатор В ТОЧКЕ позиции,
 * то есть имя ВЫЗЫВАЕМОЙ функции, а не той, чей это кадр. V8 указывает на место вызова внутри
 * вызывающей функции, поэтому подстановка дала бы `at deepInside (…)` там, где кадр на самом
 * деле принадлежит `middleFrame`. Стек, который врёт именами, хуже стека с честным `Cl`:
 * файл и строка верные, а по ним имя видно и так.
 */
async function resolveOriginalLocation(
  bundleUrl: string,
  line: number,
  column: number,
): Promise<string | null> {
  const map = await loadSourceMap(bundleUrl)
  if (!map) return null

  // error.stack считает колонку с единицы, сорсмап — с нуля. Возвращаем как было на выходе.
  const original = originalPositionFor(map, { line, column: column - 1 })
  if (original.source === null || original.line === null || original.column === null) return null

  // Sources в сорсмапе бандла обычно относительные («../../src/main.tsx» от dist/assets/) —
  // срезаем ведущие «../», чтобы в отчёте был путь от корня проекта («src/main.tsx»).
  const source = original.source.replace(/^(?:\.\.\/)+/, '')
  return `${source}:${original.line}:${original.column + 1}`
}

// Промис карты, а не саму карту: карта весит ~0.8 МБ, и несколько кадров одного бандла
// (обычное дело для одного стека) не должны тянуть и парсить её повторно. Кешируем именно
// промис, чтобы конкурентные обращения к одному URL тоже схлопывались в один fetch.
const sourceMapCache = new Map<string, Promise<TraceMap | null>>()

function loadSourceMap(bundleUrl: string): Promise<TraceMap | null> {
  let pending = sourceMapCache.get(bundleUrl)
  if (!pending) {
    pending = fetchSourceMap(bundleUrl)
    sourceMapCache.set(bundleUrl, pending)
  }
  return pending
}

async function fetchSourceMap(bundleUrl: string): Promise<TraceMap | null> {
  try {
    const response = await fetch(`${bundleUrl}.map`)
    if (!response.ok) return null
    const raw = await response.text()
    return new TraceMap(raw)
  } catch {
    // Карту не раздают в проде, пользователь оффлайн, битый JSON — не расшифровываем эту
    // карту молча, экран смерти при этом не должен пострадать.
    return null
  }
}
